/**
 * Storico dei giudizi del digest su un titolo (F-135).
 *
 * La pagina «Briefing» mostra «Titoli valutati» per un giorno solo. Da lì nasce
 * subito la domanda a cui questa pagina risponde: **come è cambiato questo
 * giudizio** — è la seconda volta che il modello dice Sell, o la prima? Il
 * prezzo obiettivo si sta alzando o inseguendo il mercato?
 *
 * ## L'ordine della pagina è l'ordine delle domande
 *
 *  1. **Timeline** — il colpo d'occhio: quanti giudizi, di che segno, con quali
 *     buchi. È la sola cosa che si legge senza leggere.
 *  2. **Prezzo obiettivo**, col prezzo di mercato sotto quando è confrontabile:
 *     un obiettivo senza il prezzo accanto non dice se è ottimismo o inseguimento.
 *  3. **Allocazione consigliata** — la stessa storia dal lato della quantità.
 *  4. **I responsi, per esteso** — dal più recente: è il testo che spiega i tre
 *     grafici, e senza sarebbero tre curve senza motivo.
 *
 * ⚠️ **Non è una misura di accuratezza.** Il confronto fra un obiettivo e ciò
 * che il titolo ha poi fatto vive nella misura degli esiti (SPEC-023), che
 * rispetta l'orizzonte e il lookahead. Qui si guarda **cosa ha detto il
 * modello**, non se avesse ragione: leggere «il prezzo ha superato l'obiettivo»
 * su questa pagina sarebbe una conclusione che questi dati non sostengono, ed è
 * scritto in fondo.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDate, formatDateTime, formatPercent } from '@/lib/format';
import { Alert, Badge, Card, EmptyState, Spinner, TableWrap } from '@/ui';
import {
  HISTORY_WINDOWS,
  useInstrumentBriefingHistory,
  type BriefingHistoryPoint,
  type BriefingRating,
} from './api';
import { BriefingHistoryChart, type HistorySeries } from './BriefingHistoryChart';
import { countRatingChanges } from './briefing-history';
import { JudgementTimeline } from './JudgementTimeline';
import './briefing.css';
import './briefing-history.css';

const RATING_TONE: Record<BriefingRating, 'positive' | 'negative' | 'neutral'> = {
  Buy: 'positive',
  Overweight: 'positive',
  Hold: 'neutral',
  Underweight: 'negative',
  Sell: 'negative',
};

const WINDOW_LABELS: Record<number, string> = { 7: '7 giorni', 30: '30 giorni', 90: '90 giorni' };

function ResponseRow({ point }: { point: BriefingHistoryPoint }) {
  return (
    <tr>
      <td>
        <Link to={`/briefing?date=${point.briefingDate}`}>{formatDate(point.briefingDate)}</Link>
        {point.revisionCount > 1 && (
          <div className="cell--minor" title={`${point.revisionCount} revisioni in giornata`}>
            revisione {point.revision}
          </div>
        )}
      </td>
      <td>
        <Badge tone={RATING_TONE[point.rating]}>{point.rating}</Badge>
      </td>
      <td className="col--secondary">
        {point.priceTarget ? (
          <>
            {formatCurrency(point.priceTarget, point.priceTargetCurrency ?? 'EUR')}
            <span className="cell--minor">{point.priceTargetFromAnalysis ? ' · da analisi' : ' · stima'}</span>
          </>
        ) : (
          <span className="cell--minor">—</span>
        )}
      </td>
      <td className="col--tertiary">
        {point.recommendedAllocationPct ? (
          formatPercent(point.recommendedAllocationPct, 2, { signed: false })
        ) : (
          <span className="cell--minor">—</span>
        )}
      </td>
      <td>{point.note}</td>
    </tr>
  );
}

export function InstrumentBriefingHistoryPage() {
  const { instrumentId } = useParams<{ instrumentId: string }>();
  const [days, setDays] = useState<number>(30);
  // ⚠️ UNA data evidenziata per tutta la pagina, non una per riquadro: passare
  // il puntatore sulla timeline accende la stessa colonna nei due grafici, e
  // viceversa. E' cio' che rende i tre riquadri una cosa sola.
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const { data, isPending, error } = useInstrumentBriefingHistory(instrumentId, days);

  if (isPending) return <Spinner label="Carico lo storico dei giudizi…" />;

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <EmptyState
        icon="🔍"
        title={notFound ? 'Titolo non trovato' : 'Storico non disponibile'}
        description={
          notFound
            ? 'Questo titolo non compare in nessuno dei tuoi digest, oppure non è più visibile.'
            : error instanceof Error
              ? error.message
              : 'Errore imprevisto.'
        }
      />
    );
  }

  const { instrument, points, briefingDays, priceScale } = data;
  const targets = points.filter((point) => point.priceTarget !== null);

  const priceSeries: HistorySeries[] = priceScale.currency
    ? [
        {
          label: 'Prezzo obiettivo',
          kind: 'primary',
          points: points.map((point) => ({ date: point.briefingDate, value: point.priceTarget })),
        },
        ...(priceScale.withCloses
          ? [
              {
                label: 'Prezzo di mercato (chiusura)',
                kind: 'secondary' as const,
                points: points.map((point) => ({ date: point.briefingDate, value: point.close })),
              },
            ]
          : []),
      ]
    : [];

  const allocationSeries: HistorySeries[] = [
    {
      label: 'Allocazione consigliata',
      kind: 'primary',
      points: points.map((point) => ({ date: point.briefingDate, value: point.recommendedAllocationPct })),
    },
  ];

  return (
    <>
      <div className="briefing-history__head">
        <div>
          <h1>
            {instrument.symbol ?? instrument.name}
            <span className="briefing-history__subject"> · storico dei giudizi</span>
          </h1>
          <p className="cell--minor">
            {instrument.name}
            {instrument.isin && ` · ISIN ${instrument.isin}`}
          </p>
        </div>
        <div className="briefing-history__actions">
          <div className="briefing-history__windows" role="group" aria-label="Periodo">
            {HISTORY_WINDOWS.map((window) => (
              <button
                key={window}
                type="button"
                className={`briefing-history__window${window === days ? ' is-active' : ''}`}
                aria-pressed={window === days}
                onClick={() => setDays(window)}
              >
                {WINDOW_LABELS[window]}
              </button>
            ))}
          </div>
          <Link className="btn btn--ghost" to="/briefing">
            Torna al digest
          </Link>
        </div>
      </div>

      {points.length === 0 ? (
        <EmptyState
          icon="📭"
          title="Nessun giudizio nel periodo"
          description={`Fra il ${formatDate(data.from)} e il ${formatDate(data.to)} nessun digest ha valutato questo titolo. Prova ad allargare il periodo.`}
        />
      ) : (
        <>
          <Card
            title="Come è andato il giudizio"
            subtitle={`${points.length} ${points.length === 1 ? 'giudizio' : 'giudizi'} fra il ${formatDate(data.from)} e il ${formatDate(data.to)} · ${countRatingChanges(points)} cambi di giudizio`}
          >
            <JudgementTimeline
              from={data.from}
              to={data.to}
              days={points.map((point) => ({
                date: point.briefingDate,
                rating: point.rating,
                revisionCount: point.revisionCount,
              }))}
              briefingDays={briefingDays}
              hoveredDate={hoveredDate}
              onHover={setHoveredDate}
            />
          </Card>

          <Card
            title="Prezzo obiettivo"
            subtitle={
              priceScale.currency
                ? `${targets.length} obiettivi in ${priceScale.currency}${priceScale.withCloses ? ', con la chiusura di mercato a confronto' : ''}`
                : undefined
            }
          >
            {priceScale.currency ? (
              <>
                <BriefingHistoryChart
                  series={priceSeries}
                  format={(value) => formatCurrency(value, priceScale.currency ?? 'EUR')}
                  label={`Prezzo obiettivo di ${instrument.symbol ?? instrument.name} nel periodo`}
                  domain={{ from: data.from, to: data.to }}
                  hoveredDate={hoveredDate}
                  onHover={setHoveredDate}
                />
                {/* La nota c'è anche quando il grafico si disegna: dice cosa NON
                    è stato sovrapposto, e perché. */}
                {priceScale.note && <p className="briefing-history__note">{priceScale.note}</p>}
              </>
            ) : (
              <Alert variant="info">{priceScale.note ?? 'Nessun prezzo obiettivo da mostrare.'}</Alert>
            )}
          </Card>

          <Card title="Allocazione consigliata" subtitle="Quota del portafoglio suggerita dal modello">
            <BriefingHistoryChart
              series={allocationSeries}
              format={(value) => formatPercent(value, 2, { signed: false })}
              label={`Allocazione consigliata per ${instrument.symbol ?? instrument.name} nel periodo`}
              domain={{ from: data.from, to: data.to }}
              hoveredDate={hoveredDate}
              onHover={setHoveredDate}
            />
          </Card>

          <Card title="I responsi, per esteso" subtitle="Dal più recente" flush>
            <TableWrap>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Giorno</th>
                    <th>Giudizio</th>
                    <th className="col--secondary">Prezzo obiettivo</th>
                    <th className="col--tertiary">Allocazione</th>
                    <th>Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {[...points].reverse().map((point) => (
                    <ResponseRow key={point.briefingDate} point={point} />
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>
        </>
      )}

      <p className="briefing__disclaimer">
        Questa pagina mostra <strong>cosa ha detto il modello</strong>, non se avesse ragione: il confronto
        con l&apos;andamento reale rispetta orizzonti e date ed è materia della misura degli esiti. Prezzi
        obiettivo e allocazioni sono stime generate da un modello linguistico e non sono consulenza
        finanziaria.
        {points[points.length - 1]?.generatedAt &&
          ` Ultimo giudizio del ${formatDateTime(points[points.length - 1]!.generatedAt)}.`}
      </p>
    </>
  );
}
